// AES-GCM encryption for admin-only data (webhook URLs, etc.)
// The encryption key is derived from the admin password and stored ONLY in memory.
// It is never written to disk. Data in localStorage is unreadable without the key.

let _adminCryptoKey: CryptoKey | null = null

const SALT = new TextEncoder().encode('brutes-vs-nerds-2025-salt')
const ITERATIONS = 100000

export async function deriveKeyFromPassword(
  password: string,
): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: SALT, iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export function setAdminCryptoKey(key: CryptoKey) {
  _adminCryptoKey = key
}

export function clearAdminCryptoKey() {
  _adminCryptoKey = null
}

export function getAdminCryptoKey(): CryptoKey | null {
  return _adminCryptoKey
}

export async function encryptData(plaintext: string): Promise<string> {
  const key = _adminCryptoKey
  if (!key) throw new Error('No admin key in memory')
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(plaintext)
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded,
  )
  const combined = new Uint8Array(12 + encrypted.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(encrypted), 12)
  return btoa(String.fromCharCode(...combined))
}

export async function decryptData(ciphertext: string): Promise<string> {
  const key = _adminCryptoKey
  if (!key) throw new Error('No admin key in memory')
  const combined = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0))
  const iv = combined.slice(0, 12)
  const data = combined.slice(12)
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    data,
  )
  return new TextDecoder().decode(decrypted)
}
